import type {
  DisplayCreative,
  EGoalType,
  Goal,
  IPredicate,
  Segment,
  TComparison,
} from '@feathr/blackbox';

/** Error the goal-step validation emits when a URL Visit goal's URL matches a creative destination. */
export const CREATIVE_URL_MATCH_ERROR: string =
  "This URL matches an ad creative's destination URL. To measure traffic here, use a Group Entry goal that tracks Ad Clicks instead.";

export const goalTypeLabels: Record<EGoalType, string> = {
  page_view: 'URL visit',
  form_submission: 'Feathr form submission',
  donation: 'Feathr donation form submission',
  group_entry: 'Group entry',
  conversion_pixel: 'Conversion pixel trigger',
};

export function getGoalSegmentName(
  goal: Goal,
  Campaigns: { get: (id: string) => { name?: string } | undefined },
): string {
  const campaignId = goal.get('parent');
  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;
  const campaignName = campaign?.name || 'Unnamed Campaign';
  const convType = goal.get('conv_type');
  return `${campaignName} - ${goalTypeLabels[convType]}`;
}

export function buildURLVisitPredicates(url: string, matchMode: TComparison): IPredicate[] {
  return [
    {
      kind: 'activity',
      attr_type: 'string',
      attr_against: 'loc_url',
      filter_type: 'all',
      comparison: matchMode,
      value: url,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];
}

/** Finds the `loc_url` predicate, handling the flat and legacy nested (`group`) structures. */
function findUrlPredicate(predicates: IPredicate[]): IPredicate | undefined {
  // .at(0) returns undefined cleanly on empty arrays; predicates[0] on a MobX
  // observable array triggers an "Out of bounds read" warning when the segment
  // has no predicates yet.
  const firstPredicate = predicates.at(0);
  if (firstPredicate?.attr_against === 'loc_url') {
    return firstPredicate;
  }
  return firstPredicate?.group?.find((predicate) => predicate.attr_against === 'loc_url');
}

/**
 * Reads the URL and match mode back out of a URL Visit goal's segment (inverse
 * of {@link buildURLVisitPredicates}). Returns `undefined` when there's no
 * parseable URL predicate.
 */
export function getUrlVisitPredicate(
  segment: Segment,
): { value: string; comparison: TComparison } | undefined {
  const urlPredicate = findUrlPredicate(segment.get('predicates', []) ?? []);
  if (typeof urlPredicate?.value !== 'string' || !urlPredicate.value) {
    return undefined;
  }
  return {
    value: urlPredicate.value,
    comparison: urlPredicate.comparison ?? 'eq',
  };
}

/**
 * Whether a URL Visit goal is subject to the creative-match rule: draft campaigns
 * check every goal; published campaigns check only goals edited this session.
 */
export function isGoalUrlSubjectToCreativeMatch(
  isDraftCampaign: boolean,
  isEditedThisSession: boolean,
): boolean {
  return isDraftCampaign || isEditedThisSession;
}

/**
 * Whether the goal URL collides with any creative destination URL. `eq` matches
 * exactly, `starts_with` by prefix. No normalization — trailing slashes, query
 * params, scheme, and casing are all significant.
 */
export function matchesCreativeDestination(
  goalUrl: string,
  matchMode: TComparison,
  creativeDestinationUrls: string[],
): boolean {
  if (!goalUrl) {
    return false;
  }
  return creativeDestinationUrls.some((destinationUrl) =>
    matchMode === 'starts_with' ? destinationUrl.startsWith(goalUrl) : destinationUrl === goalUrl,
  );
}

/**
 * Effective destination URL of each creative, deduplicated and empties removed.
 * A creative without its own `destination_url` inherits the campaign default.
 */
export function getEffectiveCreativeDestinationUrls(
  creatives: DisplayCreative[],
  campaignDestinationUrl?: string,
): string[] {
  const urls = creatives
    .map((creative) => creative.get('destination_url') || campaignDestinationUrl)
    .filter((url): url is string => !!url);
  return [...new Set(urls)];
}

export function buildFormSubmissionPredicates(formIds?: string[]): IPredicate[] {
  const basePredicates: IPredicate[] = [
    {
      kind: 'activity',
      attr_against: 'flvr',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'form_submission',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
    {
      kind: 'activity',
      attr_against: 'form_type',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'feathr_native',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];

  if (formIds && formIds.length > 0) {
    basePredicates.push({
      kind: 'activity',
      attr_against: 'form_id',
      filter_type: 'all',
      attr_type: 'list',
      comparison: 'in',
      value: formIds,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    });
  }

  return [
    {
      kind: 'activity',
      filter_type: 'all',
      excludes: false,
      group: basePredicates,
      group_mode: 'match_all',
    },
  ];
}

export function buildDonationFormSubmissionPredicates(formIds?: string[]): IPredicate[] {
  const basePredicates: IPredicate[] = [
    {
      kind: 'activity',
      attr_against: 'flvr',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'form_submission',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
    {
      kind: 'activity',
      attr_against: 'form_type',
      filter_type: 'all',
      attr_type: 'string',
      comparison: 'eq',
      value: 'donation',
      excludes: false,
      group: [],
      group_mode: 'match_all',
    },
  ];

  if (formIds && formIds.length > 0) {
    basePredicates.push({
      kind: 'activity',
      attr_against: 'form_id',
      filter_type: 'all',
      attr_type: 'list',
      comparison: 'in',
      value: formIds,
      excludes: false,
      group: [],
      group_mode: 'match_all',
    });
  }

  return [
    {
      kind: 'activity',
      filter_type: 'all',
      excludes: false,
      group: basePredicates,
      group_mode: 'match_all',
    },
  ];
}

export const goalPresets: Record<EGoalType, unknown> = {
  page_view: { attr_against: 'trgt', comparison: 'eq', value: undefined },
  group_entry: { attr_against: '', comparisons: [] },
  conversion_pixel: { attr_against: 'seg_id', comparisons: ['eq'] },
  donation: {
    group: [
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'flvr',
        comparison: 'eq',
        value: 'form_submission',
      },
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'form_type',
        comparison: 'eq',
        value: 'donation',
      },
    ],
  },
  form_submission: {
    group: [
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'flvr',
        comparison: 'eq',
        value: 'form_submission',
      },
      {
        kind: 'activity',
        attr_type: 'string',
        group_mode: 'match_all',
        attr_against: 'form_type',
        comparison: 'eq',
        value: 'feathr_native',
      },
    ],
  },
};
